import React, { useEffect, useState } from "react";
import {
  FaSave,
  FaEnvelope,
  FaPhone,
  FaTelegram,
  FaWhatsapp,
  FaInfoCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaCoins,
  FaUserFriends,
  FaPercentage,
} from "react-icons/fa";
import { getGeneralSettings, updateGeneralSettings } from "../../api/endpoints";
import "./GeneralSeting.css";
import PageHeader from "../../components/PageHeader";

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupTitle, setPopupTitle] = useState("");

  const [generalSettings, setGeneralSettings] = useState({
    _id: "",
    bonusEmail: "",
    onlineServiceNumber: "",
    telegramChannelLink: "",
    telegramCustomerServiceLink: "",
    whatsappLink: "",
    signupBonus: "",
    depositBonus: "",
    supportMessage: "",
    dailyPlayBonus: "",
    referAmount: "",
    playReferPercentage: "",
  });

  useEffect(() => {
    fetchGeneralSettings();
  }, []);

  const fetchGeneralSettings = async () => {
    try {
      setFetchLoading(true);
      const res = await getGeneralSettings();

      if (res.success && res.data) {
        setGeneralSettings({
          _id: res.data._id || "",
          bonusEmail: res.data.bonusEmail || "",
          onlineServiceNumber: res.data.onlineServiceNumber || "",
          telegramChannelLink: res.data.telegramChannelLink || "",
          telegramCustomerServiceLink:
            res.data.telegramCustomerServiceLink || "",
          whatsappLink: res.data.whatsappLink || "",
          signupBonus:
            res.data.signupBonus !== undefined ? res.data.signupBonus : "",
          dailyPlayBonus:
            res.data.dailyPlayBonus !== undefined
              ? res.data.dailyPlayBonus
              : "",
          depositBonus:
            res.data.depositBonus !== undefined ? res.data.depositBonus : "",
          referAmount:
            res.data.referAmount !== undefined ? res.data.referAmount : "",
          playReferPercentage:
            res.data.playReferPercentage !== undefined
              ? res.data.playReferPercentage
              : "",
          supportMessage: res.data.supportMessage || "",
        });
        console.log("Fetched settings:", res.data);
      }
    } catch (err) {
      console.error("Error fetching general settings:", err);
      showPopup("Error", "Failed to fetch general settings", "error");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGeneralSettings((prev) => {
      let updatedValue = value;

      // Keep numeric fields as strings so empty state is preserved
      const numericFields = [
        "signupBonus",
        "depositBonus",
        "dailyPlayBonus",
        "referAmount",
        "playReferPercentage",
      ];
      if (numericFields.includes(name)) {
        updatedValue = value === "" ? "" : value;
      }

      return {
        ...prev,
        [name]: updatedValue,
      };
    });
  };

  const showPopup = (title, message, type = "success") => {
    setPopupTitle(title);
    setPopupMessage(message);
    if (type === "success") {
      setShowSuccessPopup(true);
      setShowErrorPopup(false);
    } else {
      setShowErrorPopup(true);
      setShowSuccessPopup(false);
    }

    setTimeout(() => {
      closePopup();
    }, 5000);
  };

  const closePopup = () => {
    setShowSuccessPopup(false);
    setShowErrorPopup(false);
  };

  const handleSaveSettings = async () => {
    if (!generalSettings.bonusEmail) {
      showPopup("Validation Error", "Please enter bonus email", "error");
      return;
    }

    if (!generalSettings.onlineServiceNumber) {
      showPopup(
        "Validation Error",
        "Please enter online service number",
        "error",
      );
      return;
    }

    if (!generalSettings.supportMessage) {
      showPopup("Validation Error", "Please enter support message", "error");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(generalSettings.bonusEmail)) {
      showPopup(
        "Validation Error",
        "Please enter a valid email address",
        "error",
      );
      return;
    }

    const phoneRegex = /^\+?[0-9]{10,15}$/;
    if (
      !phoneRegex.test(generalSettings.onlineServiceNumber.replace(/\s/g, ""))
    ) {
      showPopup(
        "Validation Error",
        "Please enter a valid phone number with country code (e.g., +911234567890)",
        "error",
      );
      return;
    }

    if (
      generalSettings.telegramChannelLink &&
      !isValidUrl(generalSettings.telegramChannelLink)
    ) {
      showPopup(
        "Validation Error",
        "Please enter a valid Telegram channel link",
        "error",
      );
      return;
    }

    if (
      generalSettings.telegramCustomerServiceLink &&
      !isValidUrl(generalSettings.telegramCustomerServiceLink)
    ) {
      showPopup(
        "Validation Error",
        "Please enter a valid Telegram customer service link",
        "error",
      );
      return;
    }

    if (
      generalSettings.whatsappLink &&
      !isValidUrl(generalSettings.whatsappLink)
    ) {
      showPopup(
        "Validation Error",
        "Please enter a valid WhatsApp link",
        "error",
      );
      return;
    }

    // Numeric validations
    if (
      generalSettings.signupBonus !== "" &&
      isNaN(generalSettings.signupBonus)
    ) {
      showPopup(
        "Validation Error",
        "Signup bonus must be a valid number",
        "error",
      );
      return;
    }
    if (
      generalSettings.dailyPlayBonus !== "" &&
      isNaN(generalSettings.dailyPlayBonus)
    ) {
      showPopup(
        "Validation Error",
        "Daily play bonus must be a valid number",
        "error",
      );
      return;
    }
    if (
      generalSettings.depositBonus !== "" &&
      isNaN(generalSettings.depositBonus)
    ) {
      showPopup(
        "Validation Error",
        "Deposit bonus must be a valid number",
        "error",
      );
      return;
    }
    if (
      generalSettings.referAmount !== "" &&
      isNaN(generalSettings.referAmount)
    ) {
      showPopup(
        "Validation Error",
        "Refer amount must be a valid number",
        "error",
      );
      return;
    }
    if (
      generalSettings.playReferPercentage !== "" &&
      isNaN(generalSettings.playReferPercentage)
    ) {
      showPopup(
        "Validation Error",
        "Play refer percentage must be a valid number",
        "error",
      );
      return;
    }

    if (
      generalSettings.signupBonus !== "" &&
      Number(generalSettings.signupBonus) < 0
    ) {
      showPopup("Validation Error", "Signup bonus cannot be negative", "error");
      return;
    }
    if (
      generalSettings.depositBonus !== "" &&
      Number(generalSettings.depositBonus) < 0
    ) {
      showPopup(
        "Validation Error",
        "Deposit bonus cannot be negative",
        "error",
      );
      return;
    }
    if (
      generalSettings.referAmount !== "" &&
      Number(generalSettings.referAmount) < 0
    ) {
      showPopup("Validation Error", "Refer amount cannot be negative", "error");
      return;
    }
    if (
      generalSettings.playReferPercentage !== "" &&
      Number(generalSettings.playReferPercentage) < 0
    ) {
      showPopup(
        "Validation Error",
        "Play refer percentage cannot be negative",
        "error",
      );
      return;
    }
    if (
      generalSettings.playReferPercentage !== "" &&
      Number(generalSettings.playReferPercentage) > 100
    ) {
      showPopup(
        "Validation Error",
        "Play refer percentage cannot exceed 100",
        "error",
      );
      return;
    }

    try {
      setLoading(true);

      const updateData = {
        bonusEmail: generalSettings.bonusEmail,
        onlineServiceNumber: generalSettings.onlineServiceNumber,
        telegramChannelLink: generalSettings.telegramChannelLink || "",
        telegramCustomerServiceLink:
          generalSettings.telegramCustomerServiceLink || "",
        whatsappLink: generalSettings.whatsappLink || "",
        supportMessage: generalSettings.supportMessage,
      };

      if (generalSettings.signupBonus !== "") {
        updateData.signupBonus = Number(generalSettings.signupBonus);
      }
      if (generalSettings.dailyPlayBonus !== "") {
        updateData.dailyPlayBonus = Number(generalSettings.dailyPlayBonus);
      }
      if (generalSettings.depositBonus !== "") {
        updateData.depositBonus = Number(generalSettings.depositBonus);
      }
      if (generalSettings.referAmount !== "") {
        updateData.referAmount = Number(generalSettings.referAmount);
      }
      if (generalSettings.playReferPercentage !== "") {
        updateData.playReferPercentage = Number(
          generalSettings.playReferPercentage,
        );
      }

      console.log("Sending update data:", updateData);

      const res = await updateGeneralSettings(updateData);
      console.log("Update response:", res);

      if (res.success) {
        showPopup(
          "Success",
          res.message || "General settings updated successfully!",
          "success",
        );
        await fetchGeneralSettings();
      } else {
        showPopup("Error", res.message || "Failed to update settings", "error");
      }
    } catch (err) {
      console.error("Error updating general settings:", err);
      console.error("Error response:", err?.response?.data);
      showPopup(
        "Error",
        err?.response?.data?.message || err?.message || "Something went wrong",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  if (fetchLoading) {
    return (
      <div className="settings-page lead-page">
        <div className="row">
          <div className="col-lg-12">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page lead-page">
      <div className="row">
        <div className="col-lg-12">
          <PageHeader
            title="General Settings"
            subtitle="Manage general application settings"
          />

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <div className="row">
                {/* Bonus Email */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaEnvelope className="me-2 text-primary" />
                    Bonus Email
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="bonusEmail"
                    value={generalSettings.bonusEmail || ""}
                    onChange={handleInputChange}
                    placeholder="Enter bonus email"
                  />
                </div>

                {/* Online Service Number */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaPhone className="me-2 text-primary" />
                    Online Service Number
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="onlineServiceNumber"
                    value={generalSettings.onlineServiceNumber || ""}
                    onChange={handleInputChange}
                    placeholder="Enter phone number with country code"
                  />
                  <small className="text-muted">Format: +911234567890</small>
                </div>

                {/* Signup Bonus */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaCoins className="me-2 text-warning" />
                    Signup Bonus
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="signupBonus"
                    value={generalSettings.signupBonus ?? ""}
                    onChange={handleInputChange}
                    placeholder="Enter signup bonus amount"
                    min="0"
                    step="1"
                  />
                  <small className="text-muted">
                    Bonus amount given on user signup
                  </small>
                </div>

                {/* Daily Play Bonus */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaCoins className="me-2 text-warning" />
                    Daily Play Bonus
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="dailyPlayBonus"
                    value={generalSettings.dailyPlayBonus ?? ""}
                    onChange={handleInputChange}
                    placeholder="Enter daily play bonus amount"
                    min="0"
                    step="1"
                  />
                  <small className="text-muted">
                    Bonus amount given on daily play bonus
                  </small>
                </div>

                {/* Deposit Bonus */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaCoins className="me-2 text-success" />
                    Deposit Bonus
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="depositBonus"
                    value={generalSettings.depositBonus ?? ""}
                    onChange={handleInputChange}
                    placeholder="Enter deposit bonus amount"
                    min="0"
                    step="1"
                  />
                  <small className="text-muted">
                    Bonus amount given on user deposit
                  </small>
                </div>

                {/* Refer Amount */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaUserFriends className="me-2 text-info" />
                    Refer Amount
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="referAmount"
                    value={generalSettings.referAmount ?? ""}
                    onChange={handleInputChange}
                    placeholder="Enter refer amount"
                    min="0"
                    step="1"
                  />
                  <small className="text-muted">
                    Flat amount credited per successful referral
                  </small>
                </div>

                {/* Play Refer Percentage */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaPercentage className="me-2 text-info" />
                    Play Refer Percentage
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="playReferPercentage"
                    value={generalSettings.playReferPercentage ?? ""}
                    onChange={handleInputChange}
                    placeholder="Enter play refer percentage (%)"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <small className="text-muted">
                    Percentage of referred user's play credited as commission
                  </small>
                </div>

                {/* Telegram Channel Link */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaTelegram className="me-2 text-primary" />
                    Telegram Channel Link
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    name="telegramChannelLink"
                    value={generalSettings.telegramChannelLink || ""}
                    onChange={handleInputChange}
                    placeholder="https://t.me/your_channel"
                  />
                </div>

                {/* Telegram Customer Service Link */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaTelegram className="me-2 text-primary" />
                    Telegram Customer Service Link
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    name="telegramCustomerServiceLink"
                    value={generalSettings.telegramCustomerServiceLink || ""}
                    onChange={handleInputChange}
                    placeholder="https://t.me/your_support"
                  />
                </div>

                {/* WhatsApp Link */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    <FaWhatsapp className="me-2 text-success" />
                    WhatsApp Link
                  </label>
                  <input
                    type="url"
                    className="form-control"
                    name="whatsappLink"
                    value={generalSettings.whatsappLink || ""}
                    onChange={handleInputChange}
                    placeholder="https://wa.me/your_number"
                  />
                </div>

                {/* Support Message */}
                <div className="col-md-12 mb-3">
                  <label className="form-label fw-semibold">
                    <FaInfoCircle className="me-2 text-primary" />
                    Support Message
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="supportMessage"
                    value={generalSettings.supportMessage || ""}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Enter support message"
                  />
                </div>
              </div>

              <div className="text-end mt-3">
                <button
                  className="btn btn-primary px-4"
                  onClick={handleSaveSettings}
                  disabled={loading}
                >
                  <FaSave className="me-2" />
                  {loading ? "Saving..." : "Save Settings"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div
            className="popup-container popup-success"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-icon">
              <FaCheckCircle className="text-success" />
            </div>
            <h3 className="popup-title">{popupTitle}</h3>
            <p className="popup-message">{popupMessage}</p>
            <button className="popup-btn btn-success" onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div
            className="popup-container popup-error"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-icon">
              <FaTimesCircle className="text-danger" />
            </div>
            <h3 className="popup-title">{popupTitle}</h3>
            <p className="popup-message">{popupMessage}</p>
            <button className="popup-btn btn-danger" onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;